<template>
  <v-menu offset-y :disabled="!isDealEditable">
    <template v-slot:activator="{ on }">
      <v-chip small class="my-0"
              @click.stop.prevent
              v-on="on"
              :class="[deal.stageColor &&`deal-chip-${deal.stageColor || ''}`, contentClass || '']"
      >
        <template v-if="showDealStage && deal.stage">
          {{$i18n.DealStage[deal.stage]}}
        </template>
        <template v-else-if="deal.status">
          {{$i18n.DealStatus[deal.status]}}
        </template>
        <v-tooltip top>
          <template v-slot:activator="{on}">
            <v-icon right small v-on="on"
                    :class="[deal.stageColor && `deal-chip-${deal.stageColor || ''}`]"
                    class="ml-1 d-flex">mdi-information-outline</v-icon>
          </template>
          <span class="caption">{{$i18n.useDealChip.LAST_MODIFIED}} {{ dateTimeFormat(deal.updatedAt) }}</span>
        </v-tooltip>
        <v-flex py-0 pr-0 v-if="isDealEditable">
          <v-icon>
            mdi-menu-down
          </v-icon>
        </v-flex>
      </v-chip>
    </template>
    <v-list v-if="isDealEditable">
      <v-list-item v-for="(status, index) in getStatuses"
                   :disabled="isStatusDisabled(status.type)"
                   :key="index" @click="updateStatus(status.type)">
        {{ status.name }}
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts" setup>
import DealsApi from '@/modules/deals/services/DealsApi';
import {DealStatus} from '@/modules/deals/enums/DealStatus';
import Deal from '@/models/Deal';
import {getDictionaryByEnumObject} from '@/services/enumTranslator/GetDictionaryByEnumObject';
import EnvironmentService from '@/env/EnvironmentService';
import {computed, ref} from 'vue';
import {editableStatuses, isStatusChangeBlocked} from '@/modules/deals/enums/editableStatuses';
import { useInstance } from '@/composables/useInstance';
import { dateTimeFormat } from '@/composables/useFilters';

const emit = defineEmits<{
  (e: 'update:status'): void;
}>();

const props = defineProps<{
  deal: Deal;
  disabled?: boolean;
  showDealStage?: boolean;
  contentClass?: string;
}>();

// useInstance() opiera się na getCurrentInstance(), więc musi zostać wywołane w setupie.
// Wywołane w handlerze zdarzenia rzucało wyjątkiem PRZED requestem, więc status nigdy się nie zapisywał.
const { $errorHandler } = useInstance();

const stages = getDictionaryByEnumObject<DealStatus>(JSON.parse(JSON.stringify(DealStatus)), 'DealStatus')
  ?.filter(item => {
    return !EnvironmentService.Environment.isAppDomain().NESTO_RO || item.type !== DealStatus.RETURNED_TO_AGENT_CC;
  });

// Keep the pre-#5086 dictionary order for both display and backward-transition checks.
// Product-specific editableStatuses is only a filter, not an ordered workflow.
const statusOrder = stages.map(item => item.type);
const getStatuses = computed(() => stages.filter(item => editableStatuses(props.deal.productType!).includes(item.type)));

const isUpdatingStatus = ref<boolean>(false);

const isStatusDisabled = (statusType: DealStatus): boolean =>
  isUpdatingStatus.value || isStatusChangeBlocked(statusOrder, props.deal.status, statusType);

const isDealEditable = computed<boolean>(() => {
  return !props.disabled && props.deal.userCanEdit.STATUS;
});

// Menu zamyka się po kliknięciu, ale request jeszcze leci — bez tej blokady druga zmiana wystartowałaby
// równolegle i rollback starszego (przegranego) żądania nadpisałby status zapisany przez nowsze.
const updateStatus = async(statusType: DealStatus) => {
  if (isUpdatingStatus.value) {
    return;
  }
  isUpdatingStatus.value = true;
  const previousStatus = props.deal.status;
  props.deal.status = statusType;
  try {
    await DealsApi.updateDealStatus(props.deal.id, statusType);
    emit('update:status');
  } catch (error) {
    props.deal.status = previousStatus;
    $errorHandler(error);
  } finally {
    isUpdatingStatus.value = false;
  }
};
</script>

<!-- TODO: Kolory z V3 -->
<style scoped lang="scss">
.v-chip {
  &.deal-chip-success {
    background-color: var(--token-success-surface);

    &, .v-icon {
      color: var(--token-success-text);
    }
  }

  &.deal-chip-error {
    background-color: var(--token-error-surface);

    &, .v-icon {
      color: var(--token-error-text);
    }
  }
}
</style>
